import React, { useEffect, useState } from 'react';
import PropTypes from 'prop-types';
import { Button, Popover, Space } from 'antd';
import { WarningCircle } from '@phosphor-icons/react';
import { isRunning, notifyProjectStopped, sessionIdFor, stopService } from '../../services/SandboxService';
import { markHomeStep } from '../../services/SettingsService';
import { startProject } from './projectRun';
import StartButton, { StartAnnouncer } from './StartButton';
import { allRunning } from './startProgress';
import { clearStart, projectStartKey, runStart, stopStart, useStart } from './startRuns';
import { PlanChangeButton } from './PlanRequest';
import useRepoFlags from '../../hooks/useRepoFlags';
import { isCliItem } from '../../utils/cliServices';
import { isDeviceItem } from '../../utils/deviceApps';

/**
 * Start all / Stop all for the services of a project (project dashboard, "Run it"). `services` lists each service's
 * name and its sandbox overview entry (null when it has no session yet). Start all starts every service that does not
 * run and follows them until all of them run (the button says "Starting 1/3…", its list says where each one is), or
 * says which did not start ("2/3 running · 1 failed", with Retry and their Logs through `onOpenLogs`). A plan's
 * running-services limit stops the sequence with its own message. `onChanged` refreshes the overview as states change.
 * The start goes on when this unmounts (startRuns.js), and the Projects list's row of the project shows it too.
 * `hideDisabled` (the project Home's strip): only the one that applies, Stop all when everything runs.
 * `errorAs="popover"` (the project header, one line high): the message and "Ask for another plan" open in a small
 * popover under a short "Not started" chip, instead of a sentence that runs over the header's other buttons. It opens
 * by itself once when a new message arrives. Services flagged No build (context only) are left out of Start all
 * (Stop all still stops one that runs). Command-line tools (utils/cliServices.js) are neither counted nor waited for:
 * Start all builds one set up as one in passing; while every other service runs there is nothing left to start.
 * Native device apps (utils/deviceApps.js) are skipped the same way: built for phones, never started here.
 */
export default function RunAllActions({
  projectId, services: listed, onChanged, onOpenLogs = undefined, size = 'small', hideDisabled = false, pollMs = undefined,
  errorAs = 'inline',
}) {
  const key = projectStartKey(projectId);
  const { serviceFlags } = useRepoFlags(projectId);
  const services = listed.filter(({ serviceName, service }) => !serviceFlags(serviceName)?.noBuild || isRunning(service));
  const startable = services.filter(({ serviceName }) => !serviceFlags(serviceName)?.noBuild);
  const cli = (item) => isCliItem(projectId, item) || (isDeviceItem(projectId, item) && !isRunning(item.service));
  const { progress: last, starting } = useStart(key);
  const [stopping, setStopping] = useState(false);
  const [error, setError] = useState(null);
  const busy = starting ? 'start' : (stopping ? 'stop' : null);
  // One service (a monorepo, or a project of a single app): there is no "all" to start or stop, just the app.
  const single = services.filter((item) => !cli(item)).length === 1;
  const toStart = startable.filter((item) => !cli(item) && !isRunning(item.service));
  // Those that run or build, and those a start in progress is on its way with (the runner knows them: not 'queued').
  const going = new Set((starting ? last?.services || [] : []).filter((entry) => !['queued', 'running', 'failed', 'limit', 'stopped', 'device'].includes(entry.phase)).map((entry) => entry.serviceName));
  const toStop = services.filter(({ serviceName, service }) => isRunning(service) || going.has(serviceName));
  // What did not start, until it runs after all (started from elsewhere, or by the agent).
  const progress = !starting && last && !allRunning(last) && toStart.length === 0 ? null : last;

  // Every service of the project: those that run are counted as running, the others started (Retry: the same).
  const startAll = () => {
    setError(null);
    runStart(key, (onProgress, shouldStop) => startProject(projectId, startable, { pollMs, onEach: () => onChanged?.(), onProgress, shouldStop }))
      .then(() => { markHomeStep('previewOpened'); })
      .finally(() => onChanged?.());
  };

  const stopAll = async() => {
    setStopping(true);
    setError(null);
    // A start in progress starts no more services, and the ones it is building are cancelled by their Stop.
    stopStart(key);
    clearStart(key);
    // Whatever waits for these services (Quality's "Start and test") stops waiting.
    notifyProjectStopped(projectId);
    try {
      for (const { serviceName, service } of toStop) {
        try {
          // eslint-disable-next-line no-await-in-loop
          await stopService(service?.sessionId || sessionIdFor(projectId, serviceName));
        } catch (err) {
          setError(`Could not stop ${serviceName}: ${err?.message || 'unknown error'}`);
        }
        onChanged?.();
      }
    } finally {
      setStopping(false);
      onChanged?.();
    }
  };

  const showStart = starting || (Boolean(progress) && !allRunning(progress)) || !(hideDisabled && toStart.length === 0);
  // The plan's limit (or a refused request), once the start is over.
  const startError = !starting && progress?.done ? progress.error : null;
  const message = error || startError;
  const limitHit = Boolean(progress?.services?.some((entry) => entry.phase === 'limit'));
  const [popoverOpen, setPopoverOpen] = useState(false);
  useEffect(() => { setPopoverOpen(Boolean(message) && errorAs === 'popover'); }, [message, errorAs]);
  const problem = errorAs === 'popover' ? (message || limitHit) && (
    <Popover
      content={(
        <div className="sbx-run-problem" data-testid="run-problem">
          {message && <p className="sbx-action-error" role="alert">{message}</p>}
          {limitHit && <PlanChangeButton type="primary" />}
        </div>
      )}
      onOpenChange={setPopoverOpen}
      open={popoverOpen}
      placement="bottomLeft"
      title={limitHit ? 'Your plan\'s limit' : 'Not everything started'}
      trigger="click"
    >
      <button
        aria-label={limitHit ? 'Not started: the plan\'s limit. Details' : 'Not started. Details'}
        className="sbx-pill sbx-pill-warn sbx-pill-fixed sbx-run-problem-chip"
        data-testid="run-problem-chip"
        type="button"
      >
        <WarningCircle aria-hidden size={14} />
        <span aria-hidden="true">{limitHit ? 'Plan limit' : 'Not started'}</span>
      </button>
    </Popover>
  ) : (
    <>
      {message && <span className="sbx-action-error" role="alert">{message}</span>}
      {/* The plan's running-services limit stopped the start: ask for another plan from here. */}
      {limitHit && <PlanChangeButton />}
    </>
  );
  return (
    <>
      {/* Mounted for good, out of the flow: the button goes once everything runs (the Home shows Stop all only). */}
      <StartAnnouncer progress={progress} />
      <Space className="pd-run-all" size={6} wrap>
        {showStart && (
          <StartButton
            announce={false}
            disabled={toStart.length === 0 || Boolean(busy)}
            label={single ? 'Start' : 'Start all'}
            onDismiss={busy ? undefined : () => clearStart(key)}
            onOpenLogs={onOpenLogs}
            onRetry={busy ? undefined : startAll}
            onStart={startAll}
            progress={progress}
            size={size}
          />
        )}
        {!(hideDisabled && toStop.length === 0 && !starting) && (
          <Button
            disabled={(toStop.length === 0 && !starting) || stopping}
            loading={busy === 'stop'}
            onClick={stopAll}
            size={size}
          >
            {single ? 'Stop' : 'Stop all'}
          </Button>
        )}
        {problem}
      </Space>
    </>
  );
}

RunAllActions.propTypes = {
  projectId: PropTypes.string.isRequired,
  services: PropTypes.arrayOf(PropTypes.shape({
    serviceName: PropTypes.string.isRequired,
    service: PropTypes.object,
  })).isRequired,
  errorAs: PropTypes.oneOf(['inline', 'popover']),
  hideDisabled: PropTypes.bool,
  onChanged: PropTypes.func,
  onOpenLogs: PropTypes.func,
  size: PropTypes.oneOf(['small', 'middle', 'large']),
  /** How often a start looks at the services (tests). */
  pollMs: PropTypes.number,
};
